import { getNodeId, getNodeParentId, nodeIsLeaf } from './adapterActions';

const applyTreeChanges = (
  {
    treeId,
    models: oldModels,
    children: oldChildren,
    parents: oldParents,
    ...rest
  },
  { added, changed, removedIds },
  { adapter, plugins },
  { handleRemove: removeEnabled = true, parentId = undefined } = {},
) => {
  const models = { ...oldModels };
  const children = { ...oldChildren };
  const parents = { ...oldParents };

  const treeState = { treeId, ...rest, models, children, parents };

  if (removeEnabled) {
    handleRemove();
  }

  handleChange();

  handleAdd();

  if (
    !added.length &&
    !changed.length &&
    !removedIds.size &&
    parentId !== undefined &&
    !children[parentId]
  ) {
    children[parentId] = [];
  }

  for (const plugin of Object.values(plugins)) {
    plugin.handleDidUpdateTree &&
      plugin.handleDidUpdateTree(treeState, { adapter, plugins });
  }

  return treeState;

  function handleAdd() {
    if (!added.length) {
      return;
    }

    for (const plugin of Object.values(plugins)) {
      plugin.handleWillAddModels &&
        plugin.handleWillAddModels(treeState, added, { adapter, plugins });
    }

    addRecursively(added);

    const actuallyAdded = added.filter(m => !!models[getNodeId(adapter, m)]);

    for (const plugin of Object.values(plugins)) {
      plugin.handleDidAddModels &&
        plugin.handleDidAddModels(treeState, actuallyAdded, {
          adapter,
          plugins,
        });
    }
  }

  function addRecursively(modelsQueue, changedParentIdsSet = new Set()) {
    let addedSomething = false;
    const nextQueue = [];

    for (const model of modelsQueue) {
      const id = getNodeId(adapter, model);
      const parentId = getNodeParentId(adapter, model);

      if (parentId && !models[parentId]) {
        nextQueue.push(model);
        continue;
      }

      addedSomething = true;

      models[id] = model;
      if (!nodeIsLeaf(adapter, model)) {
        prepareChildrenForModification(id, changedParentIdsSet);
      }

      if (parentId) {
        parents[id] = parentId;
        addChild(id, parentId, changedParentIdsSet);
      } else {
        addChild(id, null, changedParentIdsSet);
      }
    }

    if (nextQueue.length) {
      if (addedSomething) {
        addRecursively(nextQueue, changedParentIdsSet);
        return;
      }

      console.warn(`Tree[${treeId}]: ignoring orphaned model `, nextQueue);
    }
  }

  function addChild(childId, parentId, changedParentIdsSet) {
    prepareChildrenForModification(parentId, changedParentIdsSet);
    children[parentId].push(childId);
  }

  function prepareChildrenForModification(parentId, changedParentIdsSet) {
    let childrenArray = children[parentId];
    if (!childrenArray) {
      childrenArray = [];
      children[parentId] = childrenArray;
      changedParentIdsSet.add(parentId);
    } else if (!changedParentIdsSet.has(parentId)) {
      childrenArray = [...childrenArray];
      children[parentId] = childrenArray;
      changedParentIdsSet.add(parentId);
    }
  }

  function handleRemove() {
    if (!removedIds.size) {
      return;
    }

    const updatedParents = new Set();

    for (const plugin of Object.values(plugins)) {
      plugin.handleWillRemoveModels &&
        plugin.handleWillRemoveModels(treeState, removedIds, {
          adapter,
          plugins,
        });
    }

    removedIds.forEach(id => {
      const parentId = parents[id] || null;
      if (!removedIds.has(parentId)) {
        updatedParents.add(parentId);
      }

      /** This check is for the Parent folder where removed Parent gets readded back */
      const isRemovedIdPartOfAdded = !!added.find(
        a => getNodeId(adapter, a) === id,
      );

      if (!isRemovedIdPartOfAdded) {
        delete children[id];
      }

      delete models[id];
      delete parents[id];
    });

    updatedParents.forEach(parentId => {
      children[parentId] = children[parentId].filter(id => !removedIds.has(id));
    });

    for (const plugin of Object.values(plugins)) {
      plugin.handleDidRemoveModels &&
        plugin.handleDidRemoveModels(treeState, removedIds, {
          adapter,
          plugins,
        });
    }
  }

  function handleChange() {
    if (!changed.length) {
      return;
    }

    for (const plugin of Object.values(plugins)) {
      plugin.handleWillChangeModels &&
        plugin.handleWillChangeModels(treeState, changed, { adapter, plugins });
    }

    changed.forEach(model => {
      models[getNodeId(adapter, model)] = model;
    });

    for (const plugin of Object.values(plugins)) {
      plugin.handleDidChangeModels &&
        plugin.handleDidChangeModels(treeState, changed, { adapter, plugins });
    }
  }
};
export default applyTreeChanges;
